import { Link, useLocation, useNavigate } from "react-router-dom";
import { useState, useEffect, useRef } from "react";
import { useCart } from "../../context/CartContext";
import { useAuth } from "../../api/hooks/useAuth";
import categoriesService from "../../api/services/categories.service";
import "./Header.css";

function CartIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path
        d="M3 4h2l2.4 10.2A2 2 0 0 0 9.35 16H18a2 2 0 0 0 1.94-1.5L21 8H7.2"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <circle cx="10" cy="20" r="1.4" fill="currentColor" />
      <circle cx="18" cy="20" r="1.4" fill="currentColor" />
    </svg>
  );
}

function MenuIcon({ open }) {
  if (open) {
    return (
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <path d="M6 6l12 12M18 6L6 18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      </svg>
    );
  }

  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M4 7h16M4 12h16M4 17h16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}

export default function Header() {
  const navigate = useNavigate();
  const { getCartCount, openSidebar } = useCart();
  const { user, logout } = useAuth();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isProductsDropdownOpen, setIsProductsDropdownOpen] = useState(false);
  const [dynamicCategories, setDynamicCategories] = useState([]);
  const location = useLocation();
  const dropdownRef = useRef(null);

  useEffect(() => {
    setIsProductsDropdownOpen(false);
    setIsMobileMenuOpen(false);
  }, [location]);

  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsProductsDropdownOpen(false);
      }
    }

    if (isProductsDropdownOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isProductsDropdownOpen]);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const res = await categoriesService.userList({ page: 0, limit: 100, search: "" });
        const body = res?.data ?? res;
        let cats = [];
        if (Array.isArray(body)) {
          cats = body;
        } else if (Array.isArray(body?.docs)) {
          cats = body.docs;
        } else if (Array.isArray(body?.data)) {
          cats = body.data;
        } else if (Array.isArray(body?.data?.docs)) {
          cats = body.data.docs;
        } else if (Array.isArray(body?.items)) {
          cats = body.items;
        }
        setDynamicCategories(cats.filter((cat) => cat.is_active !== false));
      } catch (err) {
        console.error("Failed to fetch categories:", err);
      }
    };
    fetchCategories();
  }, []);

  const isProductsPage = location.pathname === "/products";

  const navItems = [
    { name: "Home", path: "/" },
    { name: "Products", path: "/products", hasDropdown: true },
    { name: "Stores", path: "/stores" },
    { name: "About", path: "/about" },
    { name: "Lab Reports", path: "/labs" },
    { name: "FAQ", path: "/faq" },
  ];

  return (
    <>
      <header className="global-header">
        <div className="header-logo">
          <Link to="/" className="brand-link">
            <img
              src="/chilltree-images/chilltree-logo1.png"
              alt="Chill Tree"
              className="logo-image"
              width="594"
              height="420"
            />
          </Link>
        </div>

        <nav className="header-nav">
          {navItems.map((item) => {
            const isActive = item.path === "/products" ? isProductsPage : location.pathname === item.path;

            if (item.hasDropdown) {
              return (
                <div key={item.path} className="nav-dropdown" ref={dropdownRef}>
                  <button
                    type="button"
                    className={`nav-link nav-link-button ${isActive ? "active" : ""}`}
                    onClick={() => setIsProductsDropdownOpen((prev) => !prev)}
                  >
                    {item.name}
                    <span className="dropdown-arrow" aria-hidden="true">v</span>
                  </button>

                  {isProductsDropdownOpen && (
                    <div className="dropdown-menu">
                      <div className="dropdown-categories">
                        {dynamicCategories.map((category) => (
                          <button
                            key={category.uuid}
                            type="button"
                            className="dropdown-category-item"
                            onClick={() => {
                              navigate(`/products?category_uuid=${category.uuid}`);
                              setIsProductsDropdownOpen(false);
                            }}
                          >
                            <span>{category.name}</span>
                          </button>
                        ))}
                      </div>

                      <div className="dropdown-action">
                        <Link
                          to="/products"
                          className="dropdown-action-link"
                          onClick={() => setIsProductsDropdownOpen(false)}
                        >
                          View All Products
                        </Link>
                      </div>
                    </div>
                  )}
                </div>
              );
            }

            return (
              <Link
                key={item.path}
                to={item.path}
                className={`nav-link ${isActive ? "active" : ""}`}
              >
                {item.name}
              </Link>
            );
          })}
        </nav>

        <div className="header-actions">
          <button
            onClick={openSidebar}
            className="cart-icon"
            type="button"
            aria-label="Open cart"
          >
            <CartIcon />
            {getCartCount() > 0 && (
              <span className="cart-badge">{getCartCount()}</span>
            )}
          </button>

          {user ? (
            <Link to="/profile" className="btn-primary profile-link-btn">
              Profile
            </Link>
          ) : (
            <Link to="/login" className="btn-secondary">
              Login
            </Link>
          )}
        </div>

        <button
          type="button"
          className="mobile-menu-btn"
          onClick={() => setIsMobileMenuOpen((prev) => !prev)}
          aria-label="Toggle mobile menu"
        >
          <MenuIcon open={isMobileMenuOpen} />
        </button>
      </header>

      {isMobileMenuOpen && (
        <div className="mobile-menu">
          <nav className="mobile-nav">
            {navItems.map((item) => {
              const isActive = item.path === "/products" ? isProductsPage : location.pathname === item.path;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`mobile-nav-link ${isActive ? "active" : ""}`}
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  {item.name}
                </Link>
              );
            })}
          </nav>

          <div className="mobile-actions">
            <button
              onClick={() => {
                setIsMobileMenuOpen(false);
                openSidebar();
              }}
              className="mobile-cart-link mobile-action-button"
              type="button"
            >
              Cart {getCartCount() > 0 ? `(${getCartCount()})` : ""}
            </button>
            {user ? (
              <>
                <Link to="/profile" className="mobile-login-link" onClick={() => setIsMobileMenuOpen(false)}>
                  Profile
                </Link>
                <button
                  onClick={async () => {
                    setIsMobileMenuOpen(false);
                    await logout();
                    navigate('/', { replace: true });
                  }}
                  className="mobile-login-link mobile-action-button"
                  type="button"
                >
                  Logout
                </button>
              </>
            ) : (
              <Link to="/login" className="mobile-login-link" onClick={() => setIsMobileMenuOpen(false)}>
                Login
              </Link>
            )}
          </div>
        </div>
      )}
    </>
  );
}


